import { Component, HostListener, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { OidcAuthService } from 'baseflow-auth';
import { Observable, from, of, switchMap } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { IUserProfile, SpinnerComponent, fade } from 'shared';

import { WizardStepBaseComponent } from '../';
import { TWizardStepId } from '../../../interfaces';
import { UserService } from '../../../services';

@Component({
    selector: 'app-wizard-step-profile',
    templateUrl: './wizard-step-profile.component.html',
    styleUrls: ['../base/wizard-step-base.component.scss', 'wizard-step-profile.component.scss'],
    providers: [{ provide: WizardStepBaseComponent, useExisting: WizardStepProfileComponent }],
    animations: [fade()],
    imports: [FormsModule, SpinnerComponent, TranslatePipe],
})
export class WizardStepProfileComponent extends WizardStepBaseComponent {
    private readonly translate = inject(TranslateService);
    private readonly oidcAuthService = inject(OidcAuthService);
    private readonly userService = inject(UserService);

    public id: TWizardStepId = 'profile';
    public nextButtonLabel = 'back';

    protected languages: Array<string> = ['en', 'nl'];
    private initialLanguage: string;

    @HostListener('keyup.enter')
    public onSubmit(): void {
        this.wizardService.stepSubmit.emit();
    }

    public override onBeforeShow(): Observable<IUserProfile> {
        return this.oidcAuthService.user.pipe(
            switchMap((user) => {
                this.form.userId = user.id;

                return this.userService.getProfile().pipe(
                    tap(({ language, emailAddress }) => {
                        this.form.language = language;
                        this.form.emailAddress = emailAddress;
                    })
                );
            })
        );
    }

    protected setLanguage(language: string): void {
        const formControl = this.formControl.get('language');
        formControl.setValue(language);
        this.initialLanguage === language ? formControl.markAsPristine() : formControl.markAsDirty();

        this.updateNextButton();
    }

    protected updateNextButton(): void {
        this.nextButtonLabel = this.formControl.dirty ? 'updateProfile' : 'back';
    }

    protected submitWizardStep(): Observable<boolean> {
        if (!this.formControl.dirty) {
            this.goToPage.emit('logged-in');
            return of(false);
        }
        const { userId, language, emailAddress } = this.form;

        return from(this.userService.updateProfile(userId, language, emailAddress)).pipe(
            catchError((response) => {
                if (response?.error?.errorDescription === 'Invalid email address specified.')
                    this.formControl.get('emailAddress').setErrors({ email: true });

                return of(false);
            }),
            tap(() => this.translate.use(language)),
            map((response) => response !== false)
        );
    }
}
